package org.zq.astrock_ai.api

/**
 * 荐股候选市场。
 * - [A]：沪深全 A（沪主板+深主板+创业板+科创，约 5000+，不含北交所）
 * - [CYB] / [SH] / [SZ]：单市场
 * API：`boards=a` 或 `boards=cyb` / `sh` / `sz`
 */
object RecommendBoards {
    const val A = "a"
    const val CYB = "cyb"
    const val SH = "sh"
    const val SZ = "sz"
    const val KCB = "kcb"

    /** 可单选的市场（不含聚合项 a） */
    val SEGMENTS = linkedSetOf(CYB, SH, SZ, KCB)
    val ALL = linkedSetOf(A, CYB, SH, SZ)

    fun parse(raw: String?): Set<String> {
        if (raw.isNullOrBlank()) return setOf(A)
        val out = linkedSetOf<String>()
        raw.split(',', '，', ';', ' ', '|')
            .map { it.trim().lowercase() }
            .filter { it.isNotEmpty() }
            .forEach { token ->
                when (token) {
                    A, "alla", "all_a", "全a", "全市场", "全a股", "沪深", "both", "all", "全部" -> out += A
                    CYB, "chinext", "创业板", "cy" -> out += CYB
                    SH, "sse", "shanghai", "上证", "沪市", "上交所", "上证主板" -> out += SH
                    SZ, "szse", "shenzhen", "深市", "深证", "深主板", "深圳主板", "主板" -> out += SZ
                    KCB, "star", "科创", "科创板" -> out += KCB
                }
            }
        return if (out.isEmpty()) setOf(A) else out
    }

    fun encode(boards: Set<String>): String {
        val n = normalizeSelection(boards)
        return n.first()
    }

    /**
     * 规范化选择：全 A 保持 [A]；否则单市场（cyb→sh→sz→kcb 优先）。
     */
    fun normalizeSelection(boards: Set<String>?): Set<String> {
        val raw = boards.orEmpty().flatMap { parse(it) }.toSet()
        if (A in raw) return setOf(A)
        val one = SEGMENTS.firstOrNull { it in raw } ?: A
        return setOf(one)
    }

    /** @deprecated 使用 [normalizeSelection]；保留兼容旧调用 */
    fun singleBoard(boards: Set<String>?): Set<String> = normalizeSelection(boards)

    fun label(boards: Set<String>): String {
        val one = normalizeSelection(boards)
        return when {
            A in one -> "沪深全A"
            CYB in one -> "创业板"
            SH in one -> "上证主板"
            SZ in one -> "深市主板"
            KCB in one -> "科创板"
            else -> "沪深全A"
        }
    }

    /**
     * 展开为东财拉取用的板块集合。
     * 若入参已是多板块（如 cyb+sh+sz），原样保留，避免再被压成单板。
     */
    fun expandSegments(boards: Set<String>): Set<String> {
        val raw = boards.orEmpty().flatMap { parse(it) }.toSet()
        if (A in raw) return linkedSetOf(CYB, SH, SZ, KCB)
        val segs = SEGMENTS.filter { it in raw }.toSet()
        if (segs.size >= 2) return linkedSetOf<String>().also { it.addAll(segs) }
        if (segs.size == 1) return segs
        return linkedSetOf(CYB, SH, SZ, KCB)
    }

    fun cacheKey(boards: Set<String>): String =
        normalizeSelection(boards).first()

    fun minUniverseSize(boards: Set<String>): Int {
        val one = normalizeSelection(boards)
        return when {
            A in one -> 2500
            SH in one -> 800
            SZ in one -> 1000
            KCB in one -> 400
            else -> 600
        }
    }

    fun chaseThreshold(code: String): Double {
        val c = normalize(code)
        return if (isChiNext(c) || isStar(c)) 8.0 else 6.5
    }

    fun isChiNext(code: String): Boolean {
        val c = normalize(code)
        return c.startsWith("300") || c.startsWith("301")
    }

    fun isStar(code: String): Boolean {
        val c = normalize(code)
        return c.startsWith("688") || c.startsWith("689")
    }

    /** 上证主板：60xxxx（不含科创 688） */
    fun isShanghaiMain(code: String): Boolean {
        val c = normalize(code)
        return c.startsWith("60")
    }

    /** 深市主板：000/001/002/003 */
    fun isShenzhenMain(code: String): Boolean {
        val c = normalize(code)
        return c.startsWith("000") || c.startsWith("001") ||
            c.startsWith("002") || c.startsWith("003")
    }

    @Deprecated("使用 isShanghaiMain", ReplaceWith("isShanghaiMain(code)"))
    fun isShanghai(code: String): Boolean = isShanghaiMain(code)

    /** 北交所等非标准 A 股主板池 */
    fun isBeijing(code: String): Boolean {
        val c = normalize(code)
        return c.startsWith("8") || c.startsWith("4")
    }

    @Deprecated("北交所用 isBeijing；科创已纳入全A", ReplaceWith("isBeijing(code)"))
    fun isHighThreshold(code: String): Boolean = isBeijing(code) || isStar(code)

    /** 是否属于可扫描的 A 股（不含北交所） */
    fun isAShare(code: String): Boolean {
        val c = normalize(code)
        if (c.length != 6) return false
        if (isBeijing(c)) return false
        return isChiNext(c) || isShanghaiMain(c) || isShenzhenMain(c) || isStar(c)
    }

    /** 普通人默认可交易池（历史兼容：主板+创业板，不含科创/北交） */
    fun isRetailAccessible(code: String): Boolean {
        val c = normalize(code)
        if (c.length != 6) return false
        if (isBeijing(c) || isStar(c)) return false
        return isChiNext(c) || isShanghaiMain(c) || isShenzhenMain(c)
    }

    fun matches(code: String, boards: Set<String>): Boolean {
        val c = normalize(code)
        if (!isAShare(c)) return false
        val one = normalizeSelection(boards)
        if (A in one) return true
        val segs = expandSegments(one)
        val cyb = CYB in segs && isChiNext(c)
        val sh = SH in segs && isShanghaiMain(c)
        val sz = SZ in segs && isShenzhenMain(c)
        val kcb = KCB in segs && isStar(c)
        return cyb || sh || sz || kcb
    }

    /**
     * 近涨跌停硬滤：创业板/科创 ±19%；其余主板 ±9%。
     */
    fun nearLimitThreshold(code: String): Double {
        val c = normalize(code)
        return when {
            isChiNext(c) || isStar(c) -> 19.0
            else -> 9.0
        }
    }

    fun boardLimitPct(code: String): Double {
        val c = normalize(code)
        return if (isChiNext(c) || isStar(c)) 20.0 else 10.0
    }

    fun boardName(code: String): String {
        val c = normalize(code)
        return when {
            isChiNext(c) -> "创业板"
            isStar(c) -> "科创板"
            isShanghaiMain(c) -> "沪市主板"
            isShenzhenMain(c) -> "深市主板"
            else -> "其他"
        }
    }

    /** 调参/归档用板块键 */
    fun tuneBoardKey(code: String): String = when {
        isChiNext(code) -> CYB
        isStar(code) -> KCB
        isShenzhenMain(code) -> SZ
        else -> SH
    }

    private fun normalize(code: String): String =
        code.filter { it.isDigit() }.takeLast(6)
}
